import { useEffect, useState, useContext, useRef } from 'react';

import { LazyQueryResult, OperationVariables, useQuery } from '@apollo/client';

import { AnalyticsContext } from 'lane-shared/contexts';
import jwtDecode from 'jwt-decode';

import { LaneType } from 'common-types';
import { getClient } from '../apollo';
import LaunchDarklyContext from '../contexts/LaunchDarklyContext';
import { RootLevelFlagsContext } from '../contexts/RootLevelFlagContext/RootLevelFlagsContextProvider';
import { initializeUser } from '../graphql/query';
import { updateUser } from '../graphql/user';
import { isNotLoggedInError } from '../helpers';
import Storage, { StorageKeys } from '../helpers/Storage';
import { createAndUploadMedia } from '../helpers/createAndUploadMedia';
import emitter, {
  EVENT_AUTH_TOKEN_INVALID,
  EVENT_LOGOUT,
  EVENT_AUTH_TOKEN,
  EVENT_UPLOAD_PROFILE_PHOTO,
} from '../helpers/emitter';
import hasPermission from '../helpers/hasPermission';
import i18n from 'localization';
import { ImageType } from '../types/ImageType';
import { UserType } from '../types/User';
import { LibraryTypeEnum } from 'constants-content';
import { MediaImageContentTypeEnum, MediaTypeEnum } from 'constants-media';
import { PermissionKey } from 'constants-permissions';
import { GetAuthTokenResponse } from '../types/auth0/getAuthTokenResponseType';

type UseUserDataProps = {
  jti?: string;
  jwt?: string;
  blocks?: any[] | null;
  fetchBlocks?:
    | (() => Promise<LazyQueryResult<any, OperationVariables>>)
    | null;
  getAuthToken: GetAuthTokenResponse;
  /**
   * When true, storeToken will skip writing to App Storage (StorageKeys.AUTH_TOKEN).
   * Used when Auth0 is enabled and consolidate-auth-token-storage flag is ON,
   * since Auth0 manages the token directly.
   */
  skipStorageWrite?: boolean;
};

let instances = 0;

/**
 * Helper hook to set the current user, this should only be used once per app
 * . i.e. at the top level of the mobile app, or the top level of the web app.
 *
 * If you are looking for the current logged in user, use the UserDataContext
 *
 * This needs to
 *  - check for an existing authToken
 *  - get the user data from the API using that authToken
 *  - poll for the user data periodically to keep it up to date.
 *  - shut off when a logout event occurs
 *  - turn on when a login event occurs.
 */
export default function useUserData({
  jwt,
  jti,
  blocks,
  fetchBlocks,
  getAuthToken,
  skipStorageWrite = false,
}: UseUserDataProps) {
  const [user, setUser] = useState<UserType | null>(null);
  const [sessionId, setSessionId] = useState<LaneType.UUID | null>(null);
  const [route, setRoute] = useState<string | null>(null);
  const [authWarnings, setAuthWarnings] = useState<string[] | null>(null);
  const [authToken, setAuthToken] = useState<{ jti: string } | string | null>(
    null
  );
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipStorageWriteRef = useRef(skipStorageWrite);
  // has the app initialized, i.e. checked the stored for an existing auth token
  const [isInitialized, setIsInitialized] = useState(false);
  // have we attempted a login yet
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  // is there a current login attempt happening now
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // is there a current login
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // true when we invalidated the token due to me query auth error (e.g. expired/blocked). Used to
  // distinguish expired session (clear Auth0) from new SSO signup (redirect to signup).
  const [sessionInvalidatedByMeError, setSessionInvalidatedByMeError] =
    useState(false);
  // TS-38584
  const [meQuerySucceeded, setMeQuerySucceeded] = useState(false);
  const { client: lDClient } = useContext(LaunchDarklyContext);
  const { flags: rootLevelFlags } = useContext(RootLevelFlagsContext);
  const analytics = useContext(AnalyticsContext);

  useEffect(() => {
    skipStorageWriteRef.current = skipStorageWrite;
  }, [skipStorageWrite]);

  const isConsolidateAuthTokenStorageOn =
    rootLevelFlags.useAuth0ConsolidateAuthTokenStorage;

  const {
    loading,
    error,
    refetch: refetchUser,
  } = useQuery<{
    me: { user: UserType; sessionId: LaneType.UUID };
  }>(initializeUser, {
    client: getClient(),
    skip: !authToken,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (data?.me) {
        setMeQuerySucceeded(true);
      }

      if (data?.me?.user) {
        // when the user changes, update the state.
        setUser(data.me.user);
        setSessionId(data?.me?.sessionId);
        setIsLoggedIn(true);
        setIsLoggingIn(false);
        setRetryCount(0);

        if (blocks == null || blocks.length === 0) {
          if (fetchBlocks != null) {
            fetchBlocks();
          }
        }

        lDClient.setUser(data?.me?.user?._id);
        analytics.identify(data?.me?.user?._id);
        analytics.setMixpanelSessionId(data?.me?.sessionId);
      }
    },
    onError: error => {
      if (isNotLoggedInError(error)) {
        // If we have a valid authToken but getting not-logged-in error, it's can be a timing issue while creating the user login in the backend
        if (authToken && retryCount < 2) {
          setRetryCount(prev => prev + 1);

          // Clear any existing retry timeout to prevent race conditions
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }

          retryTimeoutRef.current = setTimeout(() => {
            refetchUser().catch(() => undefined /* ignore retry errors */);
          }, 1000);

          return;
        }

        // TS-38909: with the consolidate flag on, Auth0 is the single
        // source of truth for authentication state. A retry-exhausted
        // "not logged in" with an active Auth0 token means lane-server is
        // racing user provisioning (e.g. new SSO signup), not that the
        // user is unauthenticated.
        if (isConsolidateAuthTokenStorageOn) {
          if (!authToken) {
            setSessionInvalidatedByMeError(true);
            emitter.emit(EVENT_AUTH_TOKEN_INVALID);
          } else {
            setMeQuerySucceeded(true);
          }
        } else if (authToken && typeof authToken === 'string') {
          try {
            const { exp } = jwtDecode<{
              exp: number;
            }>(authToken);
            const expiringTime = exp * 1000;
            const isTokenExpired = Date.now() > expiringTime;

            if (isTokenExpired) {
              setSessionInvalidatedByMeError(true);
              emitter.emit(EVENT_AUTH_TOKEN_INVALID);
            }
            // FIXME: Log error for datadog, missing stack trace
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            setSessionInvalidatedByMeError(true);
            emitter.emit(EVENT_AUTH_TOKEN_INVALID);
          }
        } else {
          setSessionInvalidatedByMeError(true);
          emitter.emit(EVENT_AUTH_TOKEN_INVALID);
        }
      } else if (authToken) {
        // Me query failed with something other than "not logged in" (e.g. network/blocked).
        // Retry a couple times then treat as invalid session so we force logout → identifier.
        // Keep isLoggingIn true while retrying so we show loading (don't redirect to signup).
        if (retryCount < 2) {
          setRetryCount(prev => prev + 1);
          if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = setTimeout(() => {
            refetchUser().catch(() => undefined);
          }, 1000);
          return;
        }
        setSessionInvalidatedByMeError(true);
        emitter.emit(EVENT_AUTH_TOKEN_INVALID);
      }

      setIsLoggingIn(false);
    },
  });

  useEffect(() => {
    if (authToken && !user) {
      setIsLoggingIn(true);
      setHasAttemptedLogin(true);
    }

    if (!authToken) {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      setRetryCount(0);

      return; // no need to refetch if there is no token
    }

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    setRetryCount(0);

    refetchUser().catch(() => undefined /* ignore */);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    // todo: this locale logic may be more suitable elsewhere?
    async function updateUserLocale() {
      if (user?.locale) {
        try {
          await i18n.changeLanguage(user.locale);
          await Storage.setItem(StorageKeys.USER_LOCALE, user.locale);
          // FIXME: Log error for datadog, missing stack trace
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
          // error occurred changing locales not worth crashing the app over.
        }
      }
    }

    updateUserLocale();
  }, [user?.locale]);

  async function onAuthToken({ authToken, route, warnings, resolve }: any) {
    await storeToken(authToken);

    if (route) {
      setRoute(route);
    }

    if (warnings) {
      setAuthWarnings(warnings);
    }

    // callback function used to have control over the execution flow
    if (resolve) {
      resolve();
    }
  }

  async function onAuthTokenInvalid() {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    setUser(null);
    setAuthToken(null);
    setRetryCount(0);
    setMeQuerySucceeded(false);
    lDClient.unsetUser();
  }

  async function onLogout() {
    setHasAttemptedLogin(false);
    setSessionInvalidatedByMeError(false);
    onAuthTokenInvalid();

    setRoute(null);
    setIsLoggingIn(false);
    setIsLoggedIn(false);

    await Storage.removeItem(StorageKeys.PRIMARY_CHANNEL);
    await Storage.removeItem(StorageKeys.SECONDARY_CHANNEL);
  }

  async function storeToken(authToken: string) {
    if (!skipStorageWriteRef.current) {
      await Storage.setItem(StorageKeys.AUTH_TOKEN, authToken);
    }

    setSessionInvalidatedByMeError(false);
    setMeQuerySucceeded(false);
    setAuthToken(authToken);
  }

  useEffect(() => {
    let cancel = false;

    async function updateOAuthUserProfile({ profilePicture }: any) {
      // If profile picture doesn't exists return
      // the user didn't set one in their oAuth Profile
      if (!user || !profilePicture) {
        return;
      }

      const { name } = user.profile;

      try {
        const media = await createAndUploadMedia({
          selectedLibrary: {
            _id: user._id,
            type: LibraryTypeEnum.User,
            name: user.profile.name,
          },
          type: MediaTypeEnum.Image,
          name,
          media: { type: MediaImageContentTypeEnum.png } as ImageType,
          mediaBlob: profilePicture,
          thumbnail: { type: MediaImageContentTypeEnum.jpeg } as ImageType,
          thumbnailBlob: profilePicture,
        });

        if (media?._id) {
          await getClient().mutate({
            mutation: updateUser,
            variables: {
              user: {
                _id: user._id,
                profile: {
                  _id: user.profile._id,
                  image: media._id,
                },
              },
            },
          });
          await refetchUser();
        }
        // FIXME: Log error for datadog, missing stack trace
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (ex) {
        // we can swallow this, failure is tolerated
      }
    }

    // listen for login and logout events throughout the app
    emitter.addListener(EVENT_LOGOUT, onLogout);
    emitter.addListener(EVENT_AUTH_TOKEN_INVALID, onAuthTokenInvalid);
    emitter.addListener(EVENT_AUTH_TOKEN, onAuthToken);
    emitter.addListener(EVENT_UPLOAD_PROFILE_PHOTO, updateOAuthUserProfile);

    // see if there is an auth token on load.
    async function checkStorage() {
      try {
        const token = await getAuthToken();

        if (cancel) {
          return;
        }

        if (token) {
          setAuthToken(token);
          setIsInitialized(true);

          return;
        }

        // FIXME: Log error for datadog, missing stack trace
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // Tokens not found, no action required.
      }

      if (cancel) {
        return;
      }

      if (jti && jwt) {
        const searchParamToken: any = { jti, token: jwt };

        await storeToken(searchParamToken);
      } else {
        setSessionInvalidatedByMeError(false);
        setAuthToken(null);
      }

      setIsInitialized(true);
    }

    checkStorage();

    instances++;

    if (instances > 1) {
      // this is a warning to developers if they use this hook
      console.warn(
        `You should only have one instances of of useUserData running, currently ${instances}`
      );
    }

    return () => {
      cancel = true;
      instances--;

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      emitter.removeListener(EVENT_LOGOUT, onLogout);
      emitter.removeListener(EVENT_AUTH_TOKEN_INVALID, onAuthTokenInvalid);
      emitter.removeListener(EVENT_AUTH_TOKEN, onAuthToken);
      emitter.removeListener(
        EVENT_UPLOAD_PROFILE_PHOTO,
        updateOAuthUserProfile
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function hasAnyPermission(
    permissions: PermissionKey[],
    channelId?: string,
    includeAdmin?: boolean
  ): boolean {
    // We need a user and a channelId
    if (!user || !channelId) {
      return false;
    }

    return (
      user!.isSuperUser ||
      hasPermission(user!.roles, permissions, channelId, includeAdmin)
    );
  }

  return {
    refetch: refetchUser,
    user,
    loading,
    error,
    route,
    authToken,
    isInitialized,
    isLoggingIn,
    isLoggedIn,
    hasAttemptedLogin,
    sessionInvalidatedByMeError,
    meQuerySucceeded,
    authWarnings,
    setAuthWarnings,
    hasAnyPermission,
    sessionId,
  } as const;
}
